import { AppBuilderStreamAPIName } from 'src/constants';
import { buildApiEndpointUrl, getInitForApi } from 'src/utils/AmplifyApiUtils';

import type { MessagePart, SkillsConfig } from './builderSessionApi';

import { ApiMethods, ApiTags, appAPI } from '.';

export type AppBuilderStatus =
  | 'generating'
  | 'ready'
  | 'awaiting_config'
  | 'deploying'
  | 'deployed'
  | 'installed'
  | 'failed';

// Statuses where the build has produced a runnable app — everything past
// 'generating' except the terminal 'failed'. The build timeline treats any of
// these as "build complete" (defense-in-depth against out-of-order SSE) even if
// a per-task update lagged. Deliberately broader than PUBLISH_ENABLED_STATUSES:
// the timeline shows complete as soon as the app exists, whereas publishing only
// enables once the build is ready/installed/deployed.
export const BUILD_TIMELINE_COMPLETE_STATUSES: readonly AppBuilderStatus[] = [
  'ready',
  'awaiting_config',
  'deploying',
  'deployed',
  'installed',
];

// Statuses where the publish/deploy action is enabled — a strict subset of
// BUILD_TIMELINE_COMPLETE_STATUSES (see the note there for why they differ).
export const PUBLISH_ENABLED_STATUSES: readonly AppBuilderStatus[] = [
  'ready',
  'installed',
  'deployed',
];

export interface RequiredSecret {
  key: string;
  label: string;
  description: string;
  type: 'url' | 'api_key' | 'secret' | 'text';
  docsUrl?: string;
}

/**
 * Parsed `.assembly/app.json` surface manifest expressed as paths.
 * Combined with `sandboxUrl` or `deploymentUrl` to construct preview / live
 * URLs. An empty string means the surface was not declared in the manifest;
 * `surfaces` itself being undefined means no manifest has been parsed yet
 * (legacy builders, or builds that haven't completed once).
 */
export interface AppSurfaces {
  internalPath?: string;
  clientPath?: string;
  notificationPath?: string;
}

export interface QualityReport {
  score: number;
  review: string;
  evaluatedAt?: string;
}

export interface AppBuilder {
  id: string;
  object: 'appBuilder';
  name: string;
  description: string;
  icon: string;
  status: AppBuilderStatus;
  prompt: string;
  useProductAgent?: boolean;
  deploymentUrl: string;
  sandboxUrl?: string;
  // Durable connection state of the sandbox dev server. Single source of truth
  // for the reconnecting overlay — do NOT infer connection from sandboxUrl
  // (that URL is stable across stop/resume and says nothing about liveness).
  // Backend always returns a concrete value (legacy rows are derived).
  sandboxState?: SandboxConnectState;
  deployErrors?: string;
  installedAppId: string;
  installedModuleId: string;
  modelConfig?: ModelConfig;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  requiredSecrets?: RequiredSecret[];
  surfaces?: AppSurfaces;
  // Internal performance metric anchors (POR-20126). Both nullable —
  // populated lazily on first plan turn / first ready transition, so
  // older AppBuilder rows will have them undefined.
  buildStartedAt?: string;
  firstReadyAt?: string;
  // The structural layout the product agent chose. Absent when the product
  // agent was not used or did not finalize a layout. Drives the ?layout=
  // query param on the preview iframe so the sandbox's foundation page renders
  // a matching loading skeleton on first paint.
  layout?: {
    archetype: string;
    hasFilterBar: boolean;
    hasHeaderCta: boolean;
    hasTabs: boolean;
    regions: string[];
    contentWidth?: 'full' | 'narrow';
  };
  benchmarkRunId?: string;
  qualityReport?: QualityReport;
}

export interface SkillInfo {
  name: string;
  description: string;
}

export interface PromptInfo {
  filename: string;
  content: string;
}

export interface PromptSlotVersion {
  id: string;
  title: string;
  version: number;
  isDefault: boolean;
  updatedAt: string;
}

export interface PromptSlot {
  slug: string;
  label: string;
  category: string;
  defaultContent: string;
  versions: PromptSlotVersion[] | null;
}

export interface BuildConfigResponse {
  skills: SkillInfo[];
  prompts: {
    planner: PromptInfo;
    builder: PromptInfo;
  };
  slots?: PromptSlot[];
}

export interface UpdateAppBuilderInput {
  id: string;
  name: string;
}

/**
 * Reference to an uploaded attachment file. The webapp uploads files
 * directly to the user-files bucket via Amplify Storage.put at
 * level=protected, then echoes the resulting relative fileKey plus the
 * uploader's Cognito identityId back to the backend on the sendMessage
 * call. The backend reconstructs the full S3 path on its end when
 * presigning GETs for the sandbox turn program.
 */
export interface AppBuilderAttachment {
  id: string;
  name: string;
  fileKey: string;
  identityId: string;
  contentType?: string;
  size?: number;
}

export type AppBuilderMessageActionHint =
  | 'resume'
  | 'send_followup'
  | 'skip'
  | 'allow';

export interface AppBuilderMessage {
  id: string;
  content: string;
  type: string;
  role: string;
  turnId?: string;
  createdAt?: string;
  updatedAt?: string;
  finishReason?: string;
  actionHints?: AppBuilderMessageActionHint[];
  /** Raw message parts from the builder session API. Populated for task_list messages. */
  parts?: MessagePart[];
  narratorStatus?: 'forming' | 'complete';
  /** 0-based index of the thinking paragraph this narration summarizes; the
   * Decision Stream orders lines by it (stable across out-of-order completions). */
  narratorParagraphIndex?: number;
  attachments?: AppBuilderAttachment[];
}

export interface AppBuilderVersionDiff {
  versionId: string;
  versionNumber: number;
  createdAt: string;
  changedFiles: string[];
  addedFiles?: string[];
  removedFiles?: string[];
}

// Product agent types

export interface ProductAgentWizardContext {
  name: string;
  description: string;
  prompt: string;
  icon: string;
}

export interface ProductAgentMessage {
  role: 'user' | 'assistant';
  content: string;
}

export interface ProductAgentQuestionOption {
  label: string;
  description: string;
}

export interface ProductAgentQuestion {
  question: string;
  header: string;
  options: ProductAgentQuestionOption[];
  multiSelect: boolean;
  // Both default to true when undefined; the card applies the defaulting.
  allowFreeText?: boolean;
  skippable?: boolean;
}

export interface ProductAgentChatInput {
  wizardContext: ProductAgentWizardContext;
  messages: ProductAgentMessage[];
}

export interface ProductAgentResponse {
  message: string;
  questions?: ProductAgentQuestion[];
  isComplete: boolean;
  finalizedPrompt?: string;
}

export class AppBuilderStreamError extends Error {
  status?: number;

  constructor(message: string, status?: number) {
    super(message);
    this.name = 'AppBuilderStreamError';
    this.status = status;
  }
}

const getStreamErrorMessage = async (response: Response) => {
  try {
    const payload = await response.json();
    if (
      payload &&
      typeof payload === 'object' &&
      'message' in payload &&
      typeof payload.message === 'string'
    ) {
      return payload.message as string;
    }
  } catch (error) {
    // Fall through to the generic message when the response is not JSON.
  }

  return 'Unable to stream message to app builder';
};

export interface PlanTask {
  id: string;
  /** Human-readable headline shown to non-technical reviewers. */
  title: string;
  /** 1–2 sentence non-technical summary of what this task delivers. */
  userSummary: string;
  /** Technical implementation instructions for the build agent (LLM). */
  description: string;
  stepsToVerify: string[];
}

export const isPlanTask = (value: unknown): value is PlanTask => {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.id === 'string' &&
    typeof v.title === 'string' &&
    typeof v.userSummary === 'string' &&
    typeof v.description === 'string' &&
    Array.isArray(v.stepsToVerify) &&
    v.stepsToVerify.every((s) => typeof s === 'string')
  );
};

export const isPlanTaskArray = (value: unknown): value is PlanTask[] =>
  Array.isArray(value) && value.every(isPlanTask);

export interface BuildEvent {
  type:
    | 'task_started'
    | 'task_log'
    | 'task_completed'
    | 'task_failed'
    | 'deploying'
    | 'completed'
    | 'error';
  taskId?: string;
  line?: string;
  url?: string;
  message?: string;
  token?: string;
}

export interface ModelConfig {
  planningModel?: string;
  planEffort?: string;
  buildModel?: string;
  buildEffort?: string;
  noTaskList?: boolean;
}

export interface PrepareBuildOutput {
  appBuilderId: string;
  sandboxName: string;
  repoFullName: string;
  marketplaceAppId: string;
  // BuilderSession created alongside the AppBuilder; the kickoff flow
  // navigates to this session instead of issuing a separate create call.
  sessionId: string;
}

export interface StartBuildOutput {
  sessionId: string;
  activeTaskListMessageId: string;
}

export async function prepareBuild(
  appName?: string,
  modelConfig?: ModelConfig,
  prompt?: string,
  useProductAgent?: boolean,
  skillsConfig?: SkillsConfig,
  promptSelections?: Record<string, string>,
): Promise<PrepareBuildOutput> {
  const requestInit = await getInitForApi(AppBuilderStreamAPIName, {
    method: 'POST',
    body: JSON.stringify({
      appName,
      modelConfig,
      prompt,
      useProductAgent,
      ...(skillsConfig ? { skillsConfig } : {}),
      ...(promptSelections ? { promptSelections } : {}),
    }),
    headers: { 'Content-Type': 'application/json' },
  });

  const response = await fetch(
    buildApiEndpointUrl(AppBuilderStreamAPIName, '/app-builders/prepare-build'),
    requestInit,
  );

  if (!response.ok) {
    const errorMessage = await getStreamErrorMessage(response);
    throw new AppBuilderStreamError(errorMessage, response.status);
  }

  return response.json() as Promise<PrepareBuildOutput>;
}

export async function startBuild(
  appBuilderId: string,
  sessionId: string,
): Promise<StartBuildOutput> {
  const requestInit = await getInitForApi(AppBuilderStreamAPIName, {
    method: 'POST',
    body: JSON.stringify({
      sessionId,
    }),
    headers: { 'Content-Type': 'application/json' },
  });

  const response = await fetch(
    buildApiEndpointUrl(
      AppBuilderStreamAPIName,
      `/app-builders/${appBuilderId}/build`,
    ),
    requestInit,
  );

  if (!response.ok) {
    const errorMessage = await getStreamErrorMessage(response);
    throw new AppBuilderStreamError(errorMessage, response.status);
  }

  return response.json() as Promise<StartBuildOutput>;
}

export const getAppBuilderEntityTag = (id: string) => ({
  type: ApiTags.appBuilders as const,
  id,
});

export const getAppBuilderListTag = () => ({
  type: ApiTags.appBuilders as const,
  id: 'LIST',
});

export type SandboxConnectState =
  | 'connected'
  | 'connecting'
  | 'disconnected'
  | 'error';

export interface ReconnectSandboxResponse {
  // The synchronous reconnect endpoint only ever returns connected or
  // connecting; the async job later drives the builder's sandboxState to
  // connected or disconnected.
  state: 'connected' | 'connecting';
  sandboxUrl?: string;
}

export const appBuilderApi = appAPI.injectEndpoints({
  endpoints: (build) => ({
    listAppBuilders: build.query<AppBuilder[], void>({
      query: () => ({
        path: '/app-builders',
        method: ApiMethods.get,
        options: {},
      }),
      providesTags: (result) => {
        const itemTags =
          result?.map((item) => getAppBuilderEntityTag(item.id)) ?? [];
        return [getAppBuilderListTag(), ...itemTags];
      },
    }),
    getAppBuilder: build.query<AppBuilder, { id: string }>({
      query: ({ id }) => ({
        path: `/app-builders/${id}`,
        method: ApiMethods.get,
        options: {},
      }),
      providesTags: (_, __, arg) => [getAppBuilderEntityTag(arg.id)],
    }),
    deployAppBuilder: build.mutation<AppBuilder, { id: string }>({
      query: ({ id }) => ({
        path: `/app-builders/${id}/deploy`,
        method: ApiMethods.post,
        options: {},
      }),
      invalidatesTags: (_, __, arg) => [
        getAppBuilderEntityTag(arg.id),
        getAppBuilderListTag(),
      ],
    }),
    submitAppBuilderSecrets: build.mutation<
      AppBuilder,
      { id: string; values: Record<string, string> }
    >({
      query: ({ id, values }) => ({
        path: `/app-builders/${id}/secrets`,
        method: ApiMethods.post,
        options: { body: { values } },
      }),
      invalidatesTags: (_, __, arg) => [
        getAppBuilderEntityTag(arg.id),
        getAppBuilderListTag(),
      ],
    }),
    updateAppBuilder: build.mutation<AppBuilder, UpdateAppBuilderInput>({
      query: ({ id, ...body }) => ({
        path: `/app-builders/${id}`,
        method: ApiMethods.patch,
        options: { body },
      }),
      // A rename updates the linked install's label in module_settings, so the
      // installs (apps) cache that drives sidebar rendering must be refreshed.
      invalidatesTags: (_, __, arg) => [
        getAppBuilderEntityTag(arg.id),
        ApiTags.apps,
      ],
    }),
    deleteAppBuilder: build.mutation<{ success: boolean }, { id: string }>({
      query: ({ id }) => ({
        path: `/app-builders/${id}`,
        method: ApiMethods.del,
        options: {},
      }),
      // The backend's app-builder delete also removes the linked install from
      // module_settings, so invalidate the installs (apps) cache too.
      invalidatesTags: [ApiTags.appBuilders, ApiTags.apps],
    }),
    transferAppBuilder: build.mutation<
      AppBuilder,
      { id: string; destinationPortalId: string }
    >({
      query: ({ id, destinationPortalId }) => ({
        path: `/app-builders/${id}/transfer`,
        method: ApiMethods.post,
        options: { body: { destinationPortalId } },
      }),
      // A transfer moves the app out of this workspace entirely, so drop it
      // from the app-builder list and refresh the installs (apps) cache that
      // drives sidebar rendering.
      invalidatesTags: [ApiTags.appBuilders, ApiTags.apps],
    }),
    getBuildConfig: build.query<BuildConfigResponse, void>({
      query: () => ({
        path: '/app-builders/build-config',
        method: ApiMethods.get,
        options: {},
      }),
      providesTags: [{ type: ApiTags.contextItems, id: 'LIST' }],
    }),
    reconnectSandbox: build.mutation<ReconnectSandboxResponse, { id: string }>({
      query: ({ id }) => ({
        path: `/app-builders/${id}/sandbox/reconnect`,
        method: ApiMethods.post,
        options: {},
      }),
      // Refetch the builder so the persisted sandboxState (connecting) surfaces
      // immediately rather than on the next 3s poll — the overlay reads it.
      invalidatesTags: (_, __, arg) => [getAppBuilderEntityTag(arg.id)],
    }),
  }),
});

export const {
  useListAppBuildersQuery,
  useGetAppBuilderQuery,
  useDeployAppBuilderMutation,
  useSubmitAppBuilderSecretsMutation,
  useUpdateAppBuilderMutation,
  useDeleteAppBuilderMutation,
  useTransferAppBuilderMutation,
  useGetBuildConfigQuery,
  useReconnectSandboxMutation,
} = appBuilderApi;
